import {
  assertProjectDocument,
  readProjectDocument,
  type ResourceRoomProjectDocumentV2
} from "./resourceRoomDocument";

const DATABASE_NAME = "resource-room-scheduler-v2";
const DATABASE_VERSION = 1;
const PROJECT_STORE = "projects";

export class ProjectStorageError extends Error {
  readonly code: string;
  readonly causeValue: unknown;

  constructor(code: string, message: string, causeValue?: unknown) {
    super(message);
    this.name = "ProjectStorageError";
    this.code = code;
    this.causeValue = causeValue;
  }
}

export class ProjectRevisionConflictError extends ProjectStorageError {
  readonly expectedRevision: number | null;
  readonly actualRevision: number | null;

  constructor(expectedRevision: number | null, actualRevision: number | null) {
    super("REVISION_CONFLICT", "课表已在另一个标签页中更新，请刷新后重试");
    this.name = "ProjectRevisionConflictError";
    this.expectedRevision = expectedRevision;
    this.actualRevision = actualRevision;
  }
}

export interface ProjectStore {
  list(query?: string): Promise<ResourceRoomProjectDocumentV2[]>;
  get(projectId: string): Promise<ResourceRoomProjectDocumentV2 | null>;
  putIfRevision(
    document: ResourceRoomProjectDocumentV2,
    expectedRevision: number | null
  ): Promise<void>;
  delete(projectId: string): Promise<void>;
  close(): void;
}

export async function openProjectStore(): Promise<ProjectStore> {
  if (!("indexedDB" in globalThis)) {
    throw new ProjectStorageError(
      "STORAGE_UNAVAILABLE",
      "当前浏览器不支持本地课表存储，请使用最新版 Chrome 或 Edge"
    );
  }
  let database: IDBDatabase;
  try {
    database = await openDatabase();
  } catch (error) {
    throw new ProjectStorageError(
      "STORAGE_OPEN_FAILED",
      "无法打开本地课表存储，请检查浏览器隐私或站点数据设置",
      error
    );
  }
  return new IndexedDbProjectStore(database);
}

class IndexedDbProjectStore implements ProjectStore {
  constructor(private readonly database: IDBDatabase) {
    database.addEventListener("versionchange", () => database.close());
  }

  async list(query = ""): Promise<ResourceRoomProjectDocumentV2[]> {
    const transaction = this.database.transaction(PROJECT_STORE, "readonly");
    const request = transaction.objectStore(PROJECT_STORE).getAll();
    const [values] = await Promise.all([requestResult(request), transactionDone(transaction)]);
    const normalizedQuery = query.trim().toLocaleLowerCase("zh-CN");
    return values
      .map((value) => readProjectDocument(value))
      .filter(
        (document) =>
          !normalizedQuery ||
          document.name.toLocaleLowerCase("zh-CN").includes(normalizedQuery)
      )
      .sort(
        (left, right) =>
          right.created_at.localeCompare(left.created_at) ||
          right.id.localeCompare(left.id)
      );
  }

  async get(projectId: string): Promise<ResourceRoomProjectDocumentV2 | null> {
    const transaction = this.database.transaction(PROJECT_STORE, "readonly");
    const request = transaction.objectStore(PROJECT_STORE).get(projectId);
    const [value] = await Promise.all([requestResult(request), transactionDone(transaction)]);
    return value === undefined ? null : readProjectDocument(value);
  }

  async putIfRevision(
    document: ResourceRoomProjectDocumentV2,
    expectedRevision: number | null
  ): Promise<void> {
    assertProjectDocument(document);
    const normalizedDocument = structuredClone(document);
    const transaction = this.database.transaction(PROJECT_STORE, "readwrite");
    const store = transaction.objectStore(PROJECT_STORE);
    try {
      const current = await requestResult(store.get(normalizedDocument.id));
      const actualRevision =
        current === undefined
          ? null
          : readProjectDocument(current).document_revision;
      if (actualRevision !== expectedRevision) {
        transaction.abort();
        throw new ProjectRevisionConflictError(expectedRevision, actualRevision);
      }
      store.put(structuredClone(normalizedDocument));
      await transactionDone(transaction);
    } catch (error) {
      if (error instanceof ProjectStorageError) throw error;
      try {
        transaction.abort();
      } catch {
        // The transaction may already be inactive after an IndexedDB failure.
      }
      throw new ProjectStorageError(
        "STORAGE_WRITE_FAILED",
        "课表未能保存到当前浏览器，原有数据保持不变",
        error
      );
    }
  }

  async delete(projectId: string): Promise<void> {
    const transaction = this.database.transaction(PROJECT_STORE, "readwrite");
    transaction.objectStore(PROJECT_STORE).delete(projectId);
    try {
      await transactionDone(transaction);
    } catch (error) {
      throw new ProjectStorageError(
        "STORAGE_WRITE_FAILED",
        "课表未能从当前浏览器删除",
        error
      );
    }
  }

  close(): void {
    this.database.close();
  }
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.addEventListener("upgradeneeded", () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(PROJECT_STORE)) {
        database.createObjectStore(PROJECT_STORE, { keyPath: "id" });
      }
    });
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("IndexedDB open failed")),
      { once: true }
    );
    request.addEventListener(
      "blocked",
      () => reject(new Error("IndexedDB upgrade blocked")),
      { once: true }
    );
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("IndexedDB request failed")),
      { once: true }
    );
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener(
      "abort",
      () => reject(transaction.error ?? new Error("IndexedDB transaction aborted")),
      { once: true }
    );
    transaction.addEventListener(
      "error",
      () => reject(transaction.error ?? new Error("IndexedDB transaction failed")),
      { once: true }
    );
  });
}
